import { signal, type Signal } from '@preact/signals';

import type { Api } from '../api';
import type { QueuedEvent, SnapshotStore } from './store';

/** Stapelgröße; passt zur Obergrenze des Endpunkts. */
const BATCH = 200;

export interface FlushApi {
  syncChecks(events: QueuedEvent[]): Promise<
    | { ok: true; data: { results: { clientEventId: string; status: string }[] } }
    | { ok: false; kind: string }
  >;
}

/**
 * Lädt offline entschiedene Prüfungen nach.
 *
 * Grundregel: **erst löschen, wenn der Server bestätigt hat.** Ein
 * abgebrochener Upload darf keinen Einlass verlieren; im Zweifel wird ein
 * Ereignis lieber zweimal geschickt. Die `clientEventId` fängt das
 * serverseitig ab.
 */
export class QueueFlusher {
  /** Anzahl noch nicht hochgeladener Prüfungen. Die Oberfläche zeigt sie an. */
  readonly pending: Signal<number> = signal(0);
  readonly online: Signal<boolean> = signal(true);

  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;

  constructor(
    private readonly api: Pick<Api, 'syncChecks'>,
    private readonly store: SnapshotStore,
    private readonly intervalMs = 15_000,
  ) {}

  async start(): Promise<void> {
    await this.refreshCount();
    this.timer = setInterval(() => void this.flush(), this.intervalMs);

    if (typeof window !== 'undefined') {
      this.online.value = navigator.onLine !== false;
      // Sobald das Netz zurück ist, nicht erst den nächsten Takt abwarten.
      window.addEventListener('online', this.onOnline);
      window.addEventListener('offline', this.onOffline);
    }
    void this.flush();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      window.removeEventListener('offline', this.onOffline);
    }
  }

  private onOnline = (): void => {
    this.online.value = true;
    void this.flush();
  };

  private onOffline = (): void => {
    this.online.value = false;
  };

  async flush(): Promise<void> {
    // Zwei gleichzeitige Läufe würden denselben Stapel doppelt schicken.
    if (this.running) return;
    this.running = true;

    try {
      let queued = await this.store.queued();
      this.pending.value = queued.length;

      while (queued.length > 0) {
        const batch = queued.slice(0, BATCH);
        const outcome = await this.api.syncChecks(batch);

        if (!outcome.ok) {
          // Kein Netz oder Serverfehler: nichts löschen, später erneut.
          this.online.value = false;
          return;
        }

        this.online.value = true;
        // Auch Dubletten sind erledigt. Der Server kennt sie bereits.
        for (const result of outcome.data.results) {
          await this.store.dequeue(result.clientEventId);
        }

        queued = await this.store.queued();
        this.pending.value = queued.length;
      }
    } finally {
      this.running = false;
    }
  }

  private async refreshCount(): Promise<void> {
    this.pending.value = (await this.store.queued()).length;
  }
}
